// The podcasting page: how a Series becomes a feed, who can find it, how to follow it in an app, how to submit it to directories, and how to take the writing and audio out as files. Keep the entire
// import graph Node-safe: this page describes app controls but never imports the authenticated app.
// Every factual sentence carries its code citation in an adjacent comment — restyle a claim, never reword one (`docs/gnothi/splash.md`). Paths are relative to `src/gnothi/`.
import { Link as RouterLink } from 'react-router';
import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ExplainerVideo } from '../../src/ui/Components/ExplainerVideo';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import HeadphonesIcon from '@mui/icons-material/HeadphonesOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpenOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import PodcastsIcon from '@mui/icons-material/PodcastsOutlined';
import PublicIcon from '@mui/icons-material/PublicOutlined';
import RssFeedIcon from '@mui/icons-material/RssFeedOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutlineOutlined';
import { SplashChrome } from './splash-chrome';
import { Band, CtaBand, Details, FeatureCard, SectionBody, SectionHeader } from './splash-sections';
import { metaDescription, ORIGIN, splashMeta } from './series-ld';
import type { Route } from './+types/publishing';

const TITLE = 'How to Publish an AI Podcast and Export Your Series | Gnothi';
const DESCRIPTION = metaDescription(
  'Publish an AI-narrated podcast from Gnothi: turn on the RSS feed, choose who can find it, follow it in Apple Podcasts or AntennaPod, submit it to directories, and export the book.',
);
const PATH = '/publishing';
const external = { target: '_blank', rel: 'noopener noreferrer' } as const;

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    'script:ld+json': {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: TITLE,
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}`,
    },
  },
];

function Steps({ children }: { children: ReactNode }) {
  return <Box component="ol" sx={{ maxWidth: 720, mx: 'auto', mb: 2, pl: 3 }}>{children}</Box>;
}

function Step({ number, children }: { number: number; children: ReactNode }) {
  return <Typography component="li" sx={{ mb: 1 }} value={number}>{children}</Typography>;
}

/** The bullet list inside a `Details` body. Nothing sets a color: it inherits the band-correct muted role from the `SectionBody` the primitive wraps it in. */
function Points({ children }: { children: ReactNode }) {
  return <Box component="ul" sx={{ pl: 3, m: 0, '& > li': { mb: 1 } }}>{children}</Box>;
}

function Hero() {
  return (
    <Band color="dark" hero>
      <Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center' }}>
        <PodcastsIcon sx={{ fontSize: 52 }} />
        <Typography variant="overline" component="p" sx={{ color: 'splash.accentOnDark', fontWeight: 800 }}>Podcasting from Gnothi</Typography>
        <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }, maxWidth: 820 }}>
          Every Series is already a podcast. Turn the feed on when you're ready.
        </Typography>
        {/* Narration on every finished chapter: `series-features.tsx:55-56`, carrying that section's own citation. */}
        <Typography component="p" sx={{ maxWidth: 720, opacity: 0.86 }}>
          Each finished chapter arrives with narration. This page covers the RSS feed and who can find it, how to follow it in a podcast app, how to submit it to directories, and how to take the writing and audio out as files.
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'center', flexWrap: 'wrap' }}>
          {([['Feed', '#feed'], ['Who can see it', '#tiers'], ['Podcast apps', '#subscribe'], ['Directories', '#directories'], ['Rolls', '#rolls'], ['Exports', '#downloads']] as const).map(([label, href]) => (
            <Button key={href} component="a" href={href} color="inherit" size="small">{label}</Button>
          ))}
        </Stack>
        <Button component="a" href="#feed" variant="contained" size="large">Turn on the feed</Button>
        <ExplainerVideo name="podcasting" sx={{ width: '100%', maxWidth: 720, mx: 'auto', pt: 2 }} />
      </Stack>
    </Band>
  );
}

function Feed() {
  return (
    <Band id="feed">
      {/* Only `pages` and `featured` are tier-gated; `feed` is ungated — `services/routes/tts/privacy-gate.ts:11-15`. */}
      <SectionHeader icon={<RssFeedIcon />} tone="themes" title="Turn on the feed" deck="Three steps in the series' Settings. The feed is available on every account." />
      <Steps>
        {/* `web/src/ui/App/Stories/SeriesTabs.tsx` — Settings is one of the series modal's tabs; its Publishing card holds the "Who can see this show" sub-card. */}
        <Step number={1}>Open the Series, choose Settings, then open Who can see this show.</Step>
        {/* Save on a private→public change opens a confirm dialog whose checkbox is what `consent: true` means on the wire — `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:53-61,114-121`, copy at `shared/utils/podcastForm.ts:38` (`PUBLISH_RIGHTS_COPY`), enforced at `services/routes/tts/privacy-gate.ts:17-19`. */}
        <Step number={2}>Pick Feed and save. Gnothi then asks you to confirm you have the right to publish it.</Step>
        {/* Both feed addresses sit in the same window, each with a copy button — `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:70-73`. */}
        <Step number={3}>Copy the RSS address from the same window and paste it into a podcast app.</Step>
      </Steps>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* One `<item>` per finished chapter: guid `services/routes/tts/rss-feed.ts:293`, the audio enclosure `:296`, cover `:300`, duration `:304`, shownotes into `<description>`/`<itunes:summary>` `:295,303`. */}
          <FeatureCard title="Audio feed" tone="themes">What podcast apps read: cover, show description, and one episode per finished chapter with its narration and shownotes.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Second address, labelled "Text feed: whole chapters, for e-reader apps" at `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:71`. The body gate is `services/routes/tts/rss-generate.ts:69,79` — `audio.source === 'ai'` AND `entries.source === 'chapter'` AND not spilled to S3 yields the chapter text, everything else falls back to shownotes — and the `isText` branch that writes it is `services/routes/tts/rss-feed.ts:273-283` (summary in `<description>`, body in `<content:encoded>` as rendered Markdown, so inline links survive). */}
          <FeatureCard title="Text feed" tone="themes">A second address carries the same episodes as text: the whole chapter where Gnothi wrote and narrated it, shownotes for an uploaded or older episode, links intact. Paste it into an RSS reader or an e-reader that takes feeds and read the show instead of hearing it.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* `withDisclosure` appends the show line to the channel summary, which covers `<description>` and `<itunes:summary>` on both feeds, and the episode line to each audio item's `<itunes:summary>`; `buildAudioItemBody` adds it to the item's `<description>` — `services/routes/tts/rss-feed.ts:176,239-241,252,302,316,330`. */}
          <FeatureCard title="AI narration disclosure" tone="themes">Every feed's description, and each audio episode's notes, end with a line saying the audio is narrated by AI-generated synthetic voices, where listeners can read it.</FeatureCard>
        </Grid>
      </Grid>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        {/* Feed tier = anyone with the URL, no page on the site (`web/src/ui/App/Stories/Manage/PodcastToggle.tsx:31`); the public-bucket move is `shared/utils/podcastForm.ts:37` (`PUBLISH_EXPOSURE_COPY`). */}
        <Alert severity="info">
          A Feed URL stays out of public directories until you submit it, but it has no password or access controls. Anyone who receives it can open or forward it. Switching the Series back to Private cannot recall copies already downloaded elsewhere, so do not submit a friends-only feed to a public directory.
        </Alert>
      </Box>
      <Details summary="More detail: show identity and cover art">
        <Points>
          {/* Identity fields: `web/src/ui/App/Stories/Manage/ShowIdentityCard.tsx:26`; the category vocabulary is `shared/schemas/config.ts:220-224`, twenty entries whose first is the non-Apple `General`; the feed language list is `shared/utils/podcastForm.ts:24`. */}
          <li>Show identity lives on the same tab: cover art with alt text, owner name and email, website, one of 20 categories, an explicit flag, and the feed language.</li>
          {/* `shared/schemas/config.ts:204-206,208-217` (1400–3000px, clamped to the short side) and the notice the upload shows first, `web/src/ui/App/Stories/Manage/ShowIdentityCard.tsx:35-45`. */}
          <li>Directories need a square cover between 1400 and 3000 px; an off-spec upload is cropped or scaled to fit, and you are told before it is stored.</li>
          {/* Both generators are limited-release (`web/src/ui/App/Stories/Manage/useGenerate.ts:9`); the Gemini confirm is `shared/utils/podcastForm.ts:40` (`COVER_CONSENT_COPY`); both debit credits — `services/routes/tts/cover-generate.ts:39,48` and `services/routes/tts/show-description-generate.ts:115,118`. */}
          <li>Gnothi can draw the cover and write the show description. Cover generation sends the show's title, mission statement, and segment topics to Google Gemini, and you confirm that first. Both are limited-release and bill credits.</li>
          {/* No client-consumed RSS element exists for an AI disclosure, so it rides the description — `services/routes/tts/rss-feed.ts:231`. */}
          <li>Podcast apps have no dedicated field for an AI disclosure, so it rides the description.</li>
        </Points>
      </Details>
    </Band>
  );
}

function Tiers() {
  return (
    <Band id="tiers" color="grey">
      {/* The cumulative four-rung ladder — `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:28-34`. */}
      <SectionHeader icon={<LockOpenIcon />} tone="organization" title="Who can see the show" deck="Four tiers, each adding to the one before." />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {/* `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:30`. */}
          <FeatureCard title="Private" tone="organization">Only you. Audio, cover, and RSS stay in a non-public bucket.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {/* `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:31`; ungated at `services/routes/tts/privacy-gate.ts:11-15`. */}
          <FeatureCard title="Feed" tone="organization">A podcast RSS feed anyone with the URL can subscribe to. No page on the site. Available on every account.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {/* `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:32,64`; the server half is `services/routes/tts/privacy-gate.ts:14-15`. */}
          <FeatureCard title="Pages" tone="organization">Everything in Feed, plus the show's own pages on gnothi.ai. Requires a Cool account.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {/* `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:33,65`; superuser-only at `services/routes/tts/privacy-gate.ts:12-13`; the directory itself is `series.tsx:136-143`. */}
          <FeatureCard title="Featured" tone="organization" footer={<Link component={RouterLink} to="/series#featured">See the featured directory →</Link>}>
            Everything in Pages, plus a listing in the public Series directory. Curated by Gnothi; shows are invited, not self-listed.
          </FeatureCard>
        </Grid>
      </Grid>
      <Details summary="More detail: rights, slugs, and what keeps running">
        <Points>
          {/* A public→public edit returns before the consent check — `services/routes/tts/privacy-gate.ts:16-19`. */}
          <li>Moving from Private to any public tier asks you once to confirm you have the right to publish the material. Moving between public tiers later never asks again.</li>
          {/* `shared/utils/podcastForm.ts:20-21` — `MAX_SUBSCRIBE_LINKS` is 8, `SLUG_RE` is the kebab slug; both fields render at any tier (`web/src/ui/App/Stories/Manage/PodcastToggle.tsx:94-95`). */}
          <li>The show carries a custom slug of your own and up to 8 subscribe links to wherever else it lives.</li>
          {/* A show below `featured` gets `robots: noindex,follow` — `series.$seriesid.tsx:51-54`; the sitemap enumerates featured shows only (`docs/gnothi/splash.md` § Sitemap). */}
          <li>Pages-tier show pages are not indexed by search engines; Featured shows are indexed and in the sitemap.</li>
          {/* The dormancy exemption is scoped to audience, and a comped account is excluded at `:100` — `services/routes/stories/autopilot.ts:83-106`. Hence "regular account". */}
          <li>A published show on a regular account keeps generating on its schedule even if its owner stops logging in, so a subscribed feed does not go quiet.</li>
        </Points>
      </Details>
    </Band>
  );
}

function Subscribe() {
  return (
    <Band id="subscribe">
      {/* Copy the address from the Settings tab's Publishing card (`web/src/ui/App/Stories/Manage/PodcastToggle.tsx:70-73`); a Feed-tier show has no page on the site (`:31`). */}
      <SectionHeader icon={<HeadphonesIcon />} tone="themes" title="Follow the feed in a podcast app" deck="Copy the RSS address from Settings → Publishing first. Following by URL does not list the show anywhere." />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Third-party UI, not code-citable. Steps and URL checked 2026-09-21; Apple's stable guide id is `find-podcasts-iph19bb8e705`. */}
          <FeatureCard title="Apple Podcasts on iPhone or iPad" tone="themes" footer={<Link href="https://support.apple.com/guide/iphone/find-podcasts-iph19bb8e705/ios" {...external}>Official Apple instructions ↗</Link>}>
            Open Podcasts and tap Library. Tap More (•••), choose Follow a Show by URL, paste the feed address, and tap Follow.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Third-party UI, not code-citable. Steps and both URLs checked 2026-09-21. */}
          <FeatureCard title="AntennaPod on Android" tone="themes" footer={<Stack spacing={0.5}><Link href="https://antennapod.org/" {...external}>Get AntennaPod ↗</Link><Link href="https://antennapod.org/documentation/getting-started/subscribe" {...external}>Official AntennaPod instructions ↗</Link></Stack>}>
            AntennaPod is a simple open-source option. Open the lower-right ••• menu, tap + Add Podcast, choose the RSS address option, paste the feed address, preview it, and subscribe.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Same gate as the Text feed card above: `services/routes/tts/rss-generate.ts:69,79`, rendered at `services/routes/tts/rss-feed.ts:273-283`. */}
          <FeatureCard title="Read it instead" tone="themes" footer={<Link href="#feed">About the text feed →</Link>}>
            Paste the text feed address into any RSS reader, or an e-reader that follows feeds. Chapters Gnothi wrote arrive as whole articles; an uploaded or older episode arrives as shownotes.
          </FeatureCard>
        </Grid>
      </Grid>
    </Band>
  );
}

function Directories() {
  return (
    <Band id="directories" color="grey">
      {/* Featured is the one listing Gnothi does itself, and it is curated — `web/src/ui/App/Stories/Manage/PodcastToggle.tsx:33,65`. */}
      <SectionHeader icon={<PublicIcon />} tone="prompt" title="Submit the show to directories" deck="Turn on the feed first, then submit that same audio RSS address to each service separately. The Featured tier is the one listing Gnothi does for you." />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Third-party UI, not code-citable. Steps and URLs checked 2026-09-21. */}
          <FeatureCard title="YouTube and YouTube Music" tone="prompt" footer={<Stack spacing={0.5}><Link href="https://studio.youtube.com/" {...external}>Open YouTube Studio ↗</Link><Link href="https://support.google.com/youtube/answer/13973017" {...external}>Official RSS steps ↗</Link></Stack>}>
            Choose Create, New podcast, then Submit RSS feed. Verify using the email in the feed, choose which episodes to import, wait for ingestion, review them under Content → Podcasts, and publish. YouTube makes static-image videos from the artwork and audio; availability and account requirements vary.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Third-party UI, not code-citable. Steps and URLs checked 2026-09-21. */}
          <FeatureCard title="Spotify" tone="prompt" footer={<Stack spacing={0.5}><Link href="https://creators.spotify.com/" {...external}>Open Spotify for Creators ↗</Link><Link href="https://support.spotify.com/creators/article/adding-a-new-show-to-your-spotify-for-creators-account/" {...external}>Official show steps ↗</Link></Stack>}>
            Add a new show, choose Find an existing show, then Somewhere else. Enter the RSS URL and the verification code sent to the feed's email address. This keeps Gnothi as the host.
          </FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Third-party UI, not code-citable. Steps and URLs checked 2026-09-21. */}
          <FeatureCard title="Apple Podcasts" tone="prompt" footer={<Stack spacing={0.5}><Link href="https://podcastsconnect.apple.com/my-podcasts" {...external}>Open Apple Podcasts Connect ↗</Link><Link href="https://podcasters.apple.com/support/897-submit-a-show" {...external}>Official submission steps ↗</Link></Stack>}>
            Add a New Show with an RSS feed. Review the imported details, set content rights and availability, save, resolve validation warnings, then publish for Apple's review.
          </FeatureCard>
        </Grid>
      </Grid>
      {/* Subscribe links are a stored list of destinations, up to 8 (`shared/utils/podcastForm.ts:20`), edited beside the exposure ladder (`web/src/ui/App/Stories/Manage/PodcastToggle.tsx:25-27`) — nothing submits them anywhere. */}
      <SectionBody center>
        Directory approval and feed updates take their own processing time. The subscribe links you add in Gnothi are shortcuts for listeners; adding one does not submit the show.
      </SectionBody>
    </Band>
  );
}

function Rolls() {
  return (
    <Band id="rolls" color="dark">
      {/* Upload and timing live in the show's Rolls card (`web/src/ui/App/Stories/Studio/RollsPanel.tsx:90-98,164-167`); placement is a topic-change seam near the timing target (`services/tts/docker/preprocess/ad_assembly.py:41-52`). */}
      <SectionHeader icon={<CampaignIcon />} tone="themes" title="Add an intro, mid-roll, or outro" deck="Upload reusable clips under Audio → Rolls. Gnothi places them at a topic change, with the timing you choose." />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* The four slots and what each does: `shared/utils/podcastRolls.ts:7-12`; the first-chapter slot is filtered to the first episode at `services/tts/docker/preprocess/ad_assembly.py:149`. */}
          <FeatureCard title="Intro" tone="themes">A reusable opening for every episode, plus an optional one-time opening for the first episode only.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Context replay after a mid-roll, from where the sentence started: `services/tts/docker/preprocess/midroll_inject.py:2-5,27`. */}
          <FeatureCard title="Mid-roll" tone="themes">Sponsor reads, announcements, or a call to action, placed where a break belongs. After it plays, a few seconds of the sentence you were in replay so the thread picks back up.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* `post_roll` — `shared/utils/podcastRolls.ts:11`, `services/tts/docker/preprocess/midroll_inject.py:42`. */}
          <FeatureCard title="Outro" tone="themes">A reusable close for credits, announcements, or a call to action.</FeatureCard>
        </Grid>
      </Grid>
      {/* Third-party policy, not code-citable. URL checked 2026-09-21. */}
      <SectionBody center>
        Before submitting to YouTube, review or remove advertising rolls: YouTube restricts embedded advertiser-supplied ad breaks, while host-read sponsorships follow its branded-content rules.{' '}
        <Link href="https://support.google.com/youtube/answer/13525207" {...external} sx={{ color: 'splash.accentOnDark' }}>Read YouTube's RSS guidance ↗</Link>
      </SectionBody>
      <Details summary="More detail: timing, re-rendering, and system rolls">
        <Points>
          {/* Two timing modes, default 20 minutes, clamped 1–120 — `web/src/ui/App/Stories/Studio/RollsPanel.tsx:90-98`. */}
          <li>Mid-roll timing is either every N minutes (default 20, 1 to 120) or spread evenly through the episode.</li>
          {/* Staleness marking: `services/routes/tts/rolls.ts:75-85`; the re-inject panel says so on the episode itself: `web/src/ui/App/Stories/Studio/ChapterAudioDialog.tsx:184,189`. */}
          <li>Episodes already rendered keep their old audio until you re-render them. Change a roll and every episode still carrying the old cut is flagged stale, so you can see which to refresh.</li>
          {/* A System roll gets no row affordances at all, playback included — `web/src/ui/App/Stories/Studio/RollsPanel.tsx:49-51,66-77`. */}
          <li>Some rolls are supplied by Gnothi and marked System; they appear in the list with a System chip, and are not yours to play, rename, or remove.</li>
        </Points>
      </Details>
    </Band>
  );
}

function Downloads() {
  return (
    <Band id="downloads">
      {/* The book is a sub-card of Settings' Publishing card (`web/src/ui/App/Stories/Manage/ManageView.tsx` `OutSubCards`, mobile `ChapterViewer.tsx`); one chapter stays in the chapter kebab (`useChapterActions.tsx`). The book export is ownership-only and free, with no feed involved (`services/routes/stories/export.ts`). */}
      <SectionHeader icon={<DownloadIcon />} tone="organization" title="Take the writing and audio out" deck="The whole book from Settings → Publishing, one chapter from the chapter menu. None of it needs a public feed." />
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Finished chapters only, in reading order (`services/routes/stories/export.ts:36-40`); EPUB 3 with a nav document (`services/routes/stories/epub.ts:220-228`). */}
          <FeatureCard title="Book as EPUB" tone="organization">Free. Builds an EPUB 3 of the finished chapters in reading order, with a table of contents. Needs at least one finished chapter.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* The `.txt` is the chapter heading plus the raw Markdown body (`web/src/ui/App/Stories/useChapterActions.tsx:77-82`); audio and transcript come off the reader menu (`web/src/ui/App/Stories/ReaderMenu.tsx:47-48`). There is no `.md` export — "Markdown-formatted text" inside a `.txt` is exact. */}
          <FeatureCard title="Chapter text and audio" tone="organization">From any finished chapter: a .txt file with the chapter heading and Markdown-formatted text, the audio, and the transcript.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* A separate lane: upload extensions `services/routes/tts/audio-create.ts:19`, paid-unlock gate `assertAudiobookUnlock`, and `_assemble_audiobook` — no ad injection — at `:2`. Nothing renders a Series as one file. */}
          <FeatureCard title="Audiobook from your own file" tone="organization">Upload an EPUB, PDF, .txt, or .md and Gnothi narrates it as one audio file, without ads. A Series does not export as a single audiobook file; use the feed or per-chapter audio for that.</FeatureCard>
        </Grid>
      </Grid>
      <Details summary="More detail: links and limits">
        <Points>
          {/* The EPUB is presigned for 3600s — `services/routes/stories/export.ts:64`. */}
          <li>The EPUB download link lasts an hour; click Download again for a fresh one.</li>
          {/* Copy link is absent below a public tier, never greyed — `web/src/ui/App/Stories/ReaderMenu.tsx:49-50`. */}
          <li>Once the show is on a public tier, the ⋮ menu in Read & listen also has Copy link for the episode audio (Share link in the mobile app).</li>
          {/* `services/routes/tts/audio-create.ts` `assertAudiobookUnlock` (`hasPaidAccess`) on both routes. */}
          <li>Audiobooks from your own files come with the one-time unlock included with any top-up.</li>
        </Points>
      </Details>
      {/* The OPDS catalog: `services/routes/opds.ts`, `docs/gnothi/api-keys.md` § OPDS; the full guide is `ereaders.tsx`. */}
      <Alert severity="info" icon={<MenuBookIcon />} sx={{ mt: 3 }}>
        To read on an e-reader, make an E-reader API key (it comes with the one-time unlock), then add https://api.gnothiai.com/opds as an OPDS catalog with the key as the password. <Link component={RouterLink} to="/ereaders">The e-reader guide</Link> covers the Xteink X4 Pro, KOReader, the text feed and the free EPUB route.
      </Alert>
    </Band>
  );
}

function Listen() {
  return (
    <Band id="listen" color="grey">
      <SectionHeader icon={<MenuBookIcon />} tone="behavior" title="Read it or listen to it in Gnothi" deck="The default. Nothing to publish first." />
      {/* Narration lands beside the chapter automatically and publishing stays a later, separate choice — `series-features.tsx:56`; the reader and player are on web and mobile alike, `series-features.tsx:124`. Both carry their citations. */}
      <SectionBody center>
        Open a finished chapter on the web or in the mobile app to read it and play its narration. The Series can stay private while you do.
      </SectionBody>
    </Band>
  );
}

function Shows() {
  return (
    <Band id="shows">
      <SectionHeader icon={<StarOutlineIcon />} tone="prompt" title="Shows made this way" deck="The generator and the voices are described on the Series page. These are the doors in." />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Slug `series-proof.tsx:8`; the ~31-minute runtime carries `series-features.tsx:49`'s citation; the Featured tier is `series.tsx:136-143`. */}
          <FeatureCard title="Continue Without Me" tone="prompt" to="/series/continue-without-me">A continuing fiction show published from Gnothi at the Featured tier; episodes run about 31 minutes.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* The directory section and its deck: `series.tsx:142-143`; the list is fetched live at prerender (`series.tsx:38-41`). */}
          <FeatureCard title="The featured directory" tone="prompt" to="/series#featured">Every show Gnothi has invited to the Featured tier, fiction and audio courses.</FeatureCard>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Both targets are FROZEN ids on /series — `series-features.tsx:41,53`, `docs/gnothi/splash.md` § Frozen. */}
          <FeatureCard title="How the episodes get made" tone="prompt" footer={<Stack spacing={0.5}><Link component={RouterLink} to="/series#podcast-generator">The AI podcast generator →</Link><Link component={RouterLink} to="/series#tts">Voices and narration →</Link></Stack>}>
            The generator, scheduling, and the four preset and custom voices.
          </FeatureCard>
        </Grid>
      </Grid>
    </Band>
  );
}

export default function Publishing() {
  return (
    <SplashChrome active="publishing">
      <Hero />
      <Feed />
      <Tiers />
      <Subscribe />
      <Directories />
      <Rolls />
      <Downloads />
      <Listen />
      <Shows />
      <CtaBand color="grey" title="Make the next chapter" deck="Create the Series first. Its narration, feed, exports, and publishing controls stay together." label="Explore Series" to="/series" />
    </SplashChrome>
  );
}
